import React, { useId } from 'react';

export interface CircularHealthRingProps {
    /** Raw score 0-100 (will be clamped) */
    score: number;
    /** Outer square size */
    size?: number;
    /** Background (base) radius */
    baseRadius?: number;
    /** Stroke width of progress ring */
    strokeWidth?: number;
    /** When true, enlarges typography for prominent standalone usage */
    prominent?: boolean;
    /** Optional aria label override */
    ariaLabel?: string;
    /** Additional className for svg */
    className?: string;
    /** Animation duration (ms) */
    durationMs?: number;
}

/**
 * Reusable deterministic SVG circular progress ring for health / score display.
 * Extracted from DataQualityInfograph to enable shared usage (e.g., classic + cinematic parity later).
 * Behavior: clamps score, animates stroke-dashoffset with easing, includes halo + gradient stroke.
 */
export const CircularHealthRing: React.FC<CircularHealthRingProps> = ({
    score,
    size = 280,
    baseRadius = 110,
    strokeWidth = 16,
    prominent = false,
    ariaLabel,
    className = '',
    durationMs = 1250,
}) => {
    const gradientId = useId();
    const rawScore = Number.isFinite(score) ? score : 0;
    const clamped = Math.min(100, Math.max(0, rawScore));
    const ringRadius = baseRadius - 12;
    const circumference = 2 * Math.PI * ringRadius;
    const progressOffset = circumference - (clamped / 100) * circumference;
    const [animationPhase, setAnimationPhase] = React.useState<'initial' | 'full' | 'final'>('initial');

    // Start animation sequence on mount
    React.useEffect(() => {
        const timer1 = setTimeout(() => setAnimationPhase('full'), 100);
        const timer2 = setTimeout(() => setAnimationPhase('final'), 800);
        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
        };
    }, []);

    // Calculate stroke-dashoffset based on animation phase
    const getStrokeDashOffset = () => {
        switch (animationPhase) {
            case 'initial': return circumference; // No progress
            case 'full': return 0; // Full circle
            case 'final': return progressOffset; // Actual percentage
            default: return circumference;
        }
    };

    return (
        <svg
            width={size}
            height={size}
            role="img"
            aria-label={ariaLabel || `Overall data health score ${clamped}`}
            className={className}
        >
            {/* Background - same size as progress ring */}
            <circle cx={size / 2} cy={size / 2} r={ringRadius} fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth={2} />
            {/* Track */}
            <circle
                cx={size / 2}
                cy={size / 2}
                r={ringRadius}
                fill="none"
                stroke="rgba(0,0,0,0.08)"
                strokeWidth={strokeWidth}
            />
            {/* Progress */}
            <circle
                cx={size / 2}
                cy={size / 2}
                r={ringRadius}
                fill="none"
                stroke="#D31145"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={getStrokeDashOffset()}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ 
                    transition: animationPhase === 'initial' ? 'none' : `stroke-dashoffset ${animationPhase === 'full' ? 600 : durationMs}ms cubic-bezier(0.65,0,0.35,1)` 
                }}
            />
            {/* Center value */}
            <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={prominent ? 48 : 42}
                fontWeight={600}
                fill="#000"
            >
                {clamped}%
            </text>
            <text
                x="50%"
                y={size / 2 + (prominent ? 36 : 32)}
                textAnchor="middle"
                fontSize={prominent ? 16 : 13}
                fontWeight={500}
                fill="rgba(0,0,0,0.65)"
                letterSpacing="2"
            >
                HEALTH
            </text>
        </svg>
    );
};

export default CircularHealthRing;